// src/api/organisations.js
/**
 * Organisation context API client (DASH-AUTH-3).
 *
 * Talks to the regional API gateway, which proxies to the backend Organisation
 * Service. Returns the authenticated user's membership and the organisation
 * record it belongs to.
 */

import api from "./axiosInstance";

export const ORGANISATION_CONTEXT_PATH = "/v1/dashboard/organisations/me";
export const ORGANISATIONS_PATH = "/v1/dashboard/organisations";

function organisationProfilePath(organisationId) {
  return `${ORGANISATIONS_PATH}/${encodeURIComponent(
    String(organisationId || "").trim(),
  )}/profile`;
}

/**
 * Creates an organisation (bootstrap-admin only; the backend authorises).
 *
 * @param {{
 *   organisationId: string,
 *   name: string,
 *   joinSlug?: string,
 *   status?: string,
 *   productPresentationFeatureAccess?: object | null
 * }} payload
 * @returns {Promise<{ organisation: object }>}
 * @throws {import('axios').AxiosError} On validation/permission errors.
 */
export async function createOrganisation({
  organisationId,
  name,
  joinSlug,
  status,
  productPresentationFeatureAccess,
}) {
  const { data } = await api.post(ORGANISATIONS_PATH, {
    organisationId: String(organisationId || "").trim(),
    name: String(name || "").trim(),
    ...(joinSlug ? { joinSlug: String(joinSlug || "").trim() } : {}),
    ...(status ? { status } : {}),
    ...(productPresentationFeatureAccess
      ? { productPresentationFeatureAccess }
      : {}),
  });
  return data;
}

/**
 * Updates an existing organisation's feature-access profile
 * (bootstrap-admin only; the backend authorises).
 *
 * @param {string} organisationId
 * @param {{ productPresentationFeatureAccess?: object | null }} payload
 * @returns {Promise<{ organisation: object }>}
 * @throws {import('axios').AxiosError} On validation/permission errors.
 */
export async function updateOrganisationProfile(
  organisationId,
  { productPresentationFeatureAccess } = {},
) {
  const { data } = await api.patch(organisationProfilePath(organisationId), {
    ...(productPresentationFeatureAccess
      ? { productPresentationFeatureAccess }
      : {}),
  });
  return data;
}

/**
 * Fetches the authenticated user's organisation context (membership + org).
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ membership: object, organisation: object|null }>}
 */
export async function fetchOrganisationContext({ signal } = {}) {
  const { data } = await api.get(ORGANISATION_CONTEXT_PATH, { signal });
  return data;
}

/**
 * Fetches the bootstrap-admin organisation list for the current regional cell.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ organisations: object[] }>}
 */
export async function fetchOrganisations({ signal } = {}) {
  const { data } = await api.get(ORGANISATIONS_PATH, { signal });
  return data;
}
